import { ArrowBack, Campaign, List, MarkEmailRead } from '@mui/icons-material';
import {
  Container,
  Grid,
  Stack,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { routes } from '../../../config/routes';
import Image from 'next/image';
import Link from 'next/link';
import { FC, ReactNode } from 'react';
import { useRouter } from 'next/router';
import { useAuth } from '../../../context/Auth';
import { useUserCompanies } from '../../../services/hooks/useUserCompanies';
import { useTranslation } from 'next-i18next';

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

interface Props {
  children: ReactNode;
  infoSection?: ReactNode;
}

const Layout: FC<Props> = ({ children, infoSection }) => {
  const { t } = useTranslation('directory');
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const { data: userCompaniesRes } = useUserCompanies();
  const userCompanies = userCompaniesRes?.data || [];

  const path = router.pathname;

  const activeTabIndex = path.includes('/directory/list')
    ? 0
    : path.includes('/directory/purchase-announcements')
    ? 1
    : path.includes('/directory/my-announcements')
    ? 2
    : 0;

  return (
    <>
      <Box
        sx={{
          background:
            'linear-gradient(rgba(226, 231, 251, 1),  rgba(226, 231, 251, 0))',
        }}
      >
        <Container>
          <Grid container pt={mdUp ? 8 : 4} spacing={2}>
            <Grid item xs={mdUp ? 5 : 12}>
              <Stack spacing={2} alignItems="flex-start">
                <Typography variant="h3" color="textPrimary">
                  {t('directory:hero:title')}
                </Typography>
                <Typography color="textSecondary">
                  {t('directory:hero:body')}
                </Typography>
                {!isAuthenticated ? (
                  <Button
                    variant="contained"
                    LinkComponent={Link}
                    href={routes.SIGN_UP()}
                  >
                    {t('directory:hero:join')}
                  </Button>
                ) : null}
              </Stack>
            </Grid>
            <Grid item xs={mdUp ? 7 : 12}>
              <Box position="relative" minHeight={mdUp ? '250px' : '150px'}>
                <Image
                  src="/hero.svg"
                  alt="hero"
                  fill
                  style={{ objectFit: 'contain' }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Container
        sx={{
          mb: 8,
        }}
      >
        <Grid container pt={8} spacing={2}>
          <Grid item xs={6} container alignItems="center">
            <IconButton
              aria-label="delete"
              href={routes.ROOT}
              LinkComponent={Link}
            >
              <ArrowBack />
            </IconButton>
          </Grid>
          <Grid item xs={6}>
            {infoSection && !mdUp && (
              <Box flex={1} display="flex" justifyContent="flex-end" my={1}>
                {infoSection}
              </Box>
            )}
          </Grid>
          <Grid container item xs={12}>
            <Tabs
              value={activeTabIndex}
              aria-label="tabs navigation"
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{
                px: 2,
                py: 0.5,
                borderRadius: 5,
                backgroundColor: 'rgba(0,0,0,0.05)',
              }}
            >
              <Tab
                href={routes.DIRECTORY}
                LinkComponent={Link}
                icon={<List />}
                iconPosition="start"
                label={t('directory:sections:directory')}
                {...a11yProps(0)}
                sx={{ minHeight: 40 }}
              />
              <Tab
                href={routes.PURCHASE_ANNOUNCEMENTS}
                LinkComponent={Link}
                icon={<Campaign />}
                iconPosition="start"
                label={t('directory:sections:purchaseAnnouncements')}
                {...a11yProps(1)}
                sx={{ minHeight: 40 }}
              />
              {isAuthenticated && userCompanies.length > 0 ? (
                <Tab
                  href={routes.MY_ANNOUNCEMENTS}
                  LinkComponent={Link}
                  icon={<MarkEmailRead />}
                  iconPosition="start"
                  label={t('directory:sections:myAnnouncements')}
                  {...a11yProps(2)}
                  sx={{ minHeight: 40 }}
                />
              ) : null}
            </Tabs>
            {mdUp && infoSection && (
              <Box flex={1} display="flex" justifyContent="flex-end" my={1}>
                {infoSection}
              </Box>
            )}
          </Grid>

          <Grid container item xs={12} spacing={2}>
            {children}
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default Layout;
